import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Input, Select, Tag, Spin, Empty } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

const { Option } = Select

const Cases = () => {
  const { t, i18n } = useTranslation()
  const [cases, setCases] = useState([])
  const [filteredCases, setFilteredCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [categories, setCategories] = useState([])

  useEffect(() => {
    // 获取案例列表，传递语言参数
    Promise.all([
      axios.get('/api/cases', { params: { lang: i18n.language } }),
      axios.get('/api/categories')
    ]).then(([casesRes, categoriesRes]) => {
      setCases(casesRes.data)
      setFilteredCases(casesRes.data)
      setCategories(categoriesRes.data.categories)
    }).catch(error => {
      console.error('Failed to fetch cases:', error)
    }).finally(() => {
      setLoading(false)
    })
  }, [i18n.language])

  useEffect(() => {
    // 过滤案例
    let filtered = cases

    if (selectedCategory) {
      filtered = filtered.filter(c => c.category === selectedCategory)
    }

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.summary.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredCases(filtered)
  }, [searchTerm, selectedCategory, cases])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="tech-bg py-20 pt-32">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            {t('cases.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            {t('cases.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* 搜索和过滤 */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              size="large"
              placeholder={t('common.search')}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Select
              size="large"
              placeholder={t('common.filter')}
              value={selectedCategory}
              onChange={setSelectedCategory}
              allowClear
              className="w-full md:w-64"
            >
              {categories.map(cat => (
                <Option key={cat} value={cat}>{cat}</Option>
              ))}
            </Select>
          </div>
        </div>
      </section>

      {/* 案例列表 */}
      <section className="py-12">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-20">
              <Spin size="large" tip={t('cases.loading')} />
            </div>
          ) : filteredCases.length === 0 ? (
            <Empty
              description={t('cases.error')}
              className="py-20"
            />
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredCases.map((caseItem, index) => (
                <motion.div
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/cases/${caseItem.id}`}>
                    <div className="card overflow-hidden h-full flex flex-col group">
                      {/* 封面图 */}
                      <div className="relative h-48 bg-gradient-to-br from-primary-500 to-primary-700 overflow-hidden">
                        {caseItem.cover ? (
                          <img
                            src={caseItem.cover}
                            alt={caseItem.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-4xl">
                            AI
                          </div>
                        )}
                        <div className="absolute top-4 right-4">
                          <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-primary-700">
                            {caseItem.category}
                          </span>
                        </div>
                      </div>

                      {/* 内容 */}
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {caseItem.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
                          {caseItem.summary}
                        </p>
                        
                        {/* 标签 */}
                        <div className="flex flex-wrap gap-2">
                          {caseItem.tags && caseItem.tags.map(tag => (
                            <Tag key={tag} color="blue">{tag}</Tag>
                          ))}
                        </div>

                        {/* 日期 */}
                        <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                          {caseItem.date}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Cases

